/**
 * Training Dashboard Page
 *
 * The active workspace's training modules and their assignments. Training is
 * keyed to the Central workspace of the session (the server ignores any org
 * id), so there is no organization picker.
 *
 * Administration — New module, Edit, Assign, Archive / Restore, the
 * workspace's assignment list — is shown only where Central grants
 * training:write in this workspace: the same capability the server requires.
 * New module / Edit additionally need module authoring switched on for this
 * deployment (GET /capabilities → trainingAuthoring).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useListOrganizationsQuery,
  useListTrainingModulesQuery,
  useListOrgAssignmentsQuery,
  useSetTrainingModuleArchivedMutation,
  useDeleteAssignmentMutation,
  type TrainingModule,
  type TrainingAssignment,
} from '../store/api/trainingApi';
import { useGetGivenEndorsementsQuery, type Endorsement } from '../store/api/endorsementsApi';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { AcknowledgeAssignment } from '../components/endorsements/AcknowledgeAssignment';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import toast from 'react-hot-toast';
import { mutationOutcome } from '../utils/mutationOutcome';
import {
  ModuleStatusBadge,
  TRAINING_ROUTES,
  TrainingErrorNotice,
  VersionLabel,
} from '../components/training/trainingUi';

/**
 * Where this page's links go. Organizations and members are Central's (local
 * org writes are retired with a 410), managed in the workspace settings tabs.
 */
export const TRAINING_LINKS = {
  workspaceSettings: '/settings/workspace',
  members: '/settings/members',
  analytics: (orgSlug: string) => `/org/${orgSlug}/analytics`,
  newModule: TRAINING_ROUTES.newModule,
  editModule: TRAINING_ROUTES.editModule,
  assignModule: TRAINING_ROUTES.assignModule,
} as const;

export function TrainingDashboardPage() {
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Same checks as the routes: Central's training:write in this workspace.
  const { hasCapability } = useCentralConsumer();
  const canManage = hasCapability('training', 'write');
  // Org analytics show every member's results: the backend requires Central's
  // org:admin in this workspace (orgAnalyticsRoutes), so the link does too.
  const canSeeAnalytics = hasCapability('org', 'admin');
  const { data: serverCaps } = useGetCapabilitiesQuery();
  const authoringOn = serverCaps?.trainingAuthoring === true;
  const canAuthor = canManage && authoringOn;

  const {
    data: modules,
    isLoading: modulesLoading,
    isError: modulesFailed,
    error: modulesError,
    refetch: refetchModules,
  } = useListTrainingModulesQuery(canManage && showArchived ? { includeArchived: true } : undefined);

  const { data: assignments } = useListOrgAssignmentsQuery(
    selectedModuleId ? { moduleId: selectedModuleId } : undefined,
    { skip: !canManage },
  );

  // The org analytics pages are keyed by the workspace org's slug (GET /orgs
  // returns only the token workspace's org).
  const { data: organizations } = useListOrganizationsQuery(undefined, { skip: !canSeeAnalytics });
  const analyticsSlug = canSeeAnalytics ? organizations?.[0]?.slug : undefined;

  const [setArchived, archiveState] = useSetTrainingModuleArchivedMutation();
  const [deleteAssignment, deleteState] = useDeleteAssignmentMutation();

  // Acknowledgement (P4.1): Central's dedicated endorsement:write in this
  // workspace, the same capability the server requires, and only where the
  // server has endorsement switched on. Workspace administration is not it.
  const canAcknowledge = serverCaps?.endorsements === true && hasCapability('endorsement', 'write');
  const { data: given } = useGetGivenEndorsementsQuery(undefined, { skip: !canAcknowledge });
  const liveAcknowledgement = new Map<string, Endorsement>(
    (given ?? [])
      .filter((e) => e.evidenceKind === 'training_assignment' && e.revokedAt === null)
      .map((e) => [e.evidenceId, e]),
  );

  const handleWithdraw = async (assignmentId: string) => {
    await mutationOutcome(
      deleteAssignment(assignmentId),
      'The assignment could not be withdrawn. Please try again.',
      toast.error,
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Training</h1>
          <p className="mt-1 text-gray-500">
            {canManage ? 'Author, publish and assign training modules for your workspace' : 'Training modules available in your workspace'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {analyticsSlug && (
            <Link to={TRAINING_LINKS.analytics(analyticsSlug)} className="btn btn-secondary">
              Analytics
            </Link>
          )}
          {canAuthor && (
            <Link to={TRAINING_LINKS.newModule} className="btn btn-primary" data-testid="training-new-module">
              New module
            </Link>
          )}
        </div>
      </div>

      {canManage && !authoringOn && serverCaps && (
        <p className="text-sm text-gray-500" data-testid="training-authoring-unavailable">
          Creating and editing training modules is not switched on for this deployment yet. Existing modules can still be
          assigned and archived.
        </p>
      )}

      <TrainingErrorNotice error={archiveState.error ?? deleteState.error} />

      {/* Stats */}
      {canManage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Active Training Modules" value={modules?.filter((m) => m.isActive).length || 0} />
          <StatCard label="Team Members" value={null} href={TRAINING_LINKS.members} hint="Managed in Settings" />
          <StatCard
            label="Active Assignments"
            value={assignments?.filter((a) => a.status === 'in_progress' || a.status === 'assigned').length || 0}
          />
          <StatCard label="Completed" value={assignments?.filter((a) => a.status === 'completed').length || 0} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Modules */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="font-semibold">Training Modules</h2>
              {canManage && (
                <label className="text-xs text-gray-500 flex items-center gap-1">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                  Show archived
                </label>
              )}
            </div>
            <div className="card-body p-0">
              {modulesLoading ? (
                <div className="p-4">
                  <div className="animate-pulse space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded" />
                    ))}
                  </div>
                </div>
              ) : modulesFailed ? (
                <div className="p-4">
                  <LoadErrorNotice
                    error={modulesError}
                    onRetry={() => void refetchModules()}
                    failedMessage="Training modules could not be loaded."
                    testIdPrefix="training-modules"
                  />
                </div>
              ) : modules?.length ? (
                <div className="divide-y">
                  {modules.map((module) => (
                    <ModuleCard
                      key={module.id}
                      module={module}
                      isSelected={selectedModuleId === module.id}
                      canManage={canManage}
                      canAuthor={canAuthor}
                      onSelect={() => setSelectedModuleId(module.id === selectedModuleId ? null : module.id)}
                      onArchive={(archived) => void setArchived({ moduleId: module.id, archived })}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center" data-testid="training-modules-empty">
                  <p className="text-sm text-gray-500 mb-2">No training modules yet</p>
                  {canAuthor ? (
                    <Link to={TRAINING_LINKS.newModule} className="btn btn-sm btn-primary">
                      Create the first module
                    </Link>
                  ) : (
                    <p className="text-xs text-gray-500">Modules appear here once someone who manages training publishes one.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Assignments (training administration only) */}
        {canManage && (
          <div className="lg:col-span-2">
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h2 className="font-semibold">
                  {selectedModuleId
                    ? `Assignments for: ${modules?.find((m) => m.id === selectedModuleId)?.name ?? ''}`
                    : 'All Assignments'}
                </h2>
              </div>
              <div className="card-body p-0">
                {assignments?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <th className="px-4 py-3">Assignee</th>
                          <th className="px-4 py-3">Module</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Score</th>
                          <th className="px-4 py-3">Due Date</th>
                          {canAcknowledge && <th className="px-4 py-3">Acknowledgement</th>}
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {assignments.map((assignment) => (
                          <AssignmentRow
                            key={assignment.id}
                            assignment={assignment}
                            canAcknowledge={canAcknowledge}
                            acknowledgement={liveAcknowledgement.get(assignment.id)}
                            onWithdraw={() => void handleWithdraw(assignment.id)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-sm text-gray-500">
                      {selectedModuleId
                        ? 'No assignments for this module yet'
                        : 'No assignments yet. Publish a module, then assign it to workspace members.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, href, hint }: { label: string; value: number | null; href?: string; hint?: string }) {
  const content = (
    <div className={`p-4 rounded-lg bg-gray-50 text-gray-600 ${href ? 'hover:opacity-80 transition-opacity' : ''}`}>
      <div className="text-sm font-medium mb-2">{label}</div>
      <div className="text-2xl font-bold text-gray-900">{value ?? '→'}</div>
      {hint && <div className="text-xs text-gray-500">{hint}</div>}
    </div>
  );
  return href ? <Link to={href}>{content}</Link> : content;
}

interface ModuleCardProps {
  module: TrainingModule;
  isSelected: boolean;
  canManage: boolean;
  canAuthor: boolean;
  onSelect: () => void;
  onArchive: (archived: boolean) => void;
}

function ModuleCard({ module, isSelected, canManage, canAuthor, onSelect, onArchive }: ModuleCardProps) {
  const assignable = module.isActive && module.status !== 'draft';
  return (
    <div
      className={`p-4 cursor-pointer transition-colors ${isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
      onClick={onSelect}
      data-testid="training-module-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate">{module.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <ModuleStatusBadge module={module} />
            {module.latestVersion && (
              <VersionLabel version={module.latestVersion.version} contentHash={module.latestVersion.contentHash} />
            )}
          </div>
          {module.assignmentStats && (
            <p className="text-sm text-gray-500 mt-1">
              {module.assignmentCount} assigned &bull; {module.completedCount} completed
            </p>
          )}
        </div>
      </div>
      {canManage && (
        <div className="mt-2 flex flex-wrap gap-3 text-sm" onClick={(e) => e.stopPropagation()}>
          {canAuthor && module.isActive && (
            <Link to={TRAINING_LINKS.editModule(module.id)} className="text-primary-600 hover:underline">
              Edit
            </Link>
          )}
          {assignable && (
            <Link to={TRAINING_LINKS.assignModule(module.id)} className="text-primary-600 hover:underline">
              Assign
            </Link>
          )}
          <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => onArchive(module.isActive)}>
            {module.isActive ? 'Archive' : 'Restore'}
          </button>
        </div>
      )}
    </div>
  );
}

interface AssignmentRowProps {
  assignment: TrainingAssignment;
  canAcknowledge: boolean;
  acknowledgement?: Endorsement;
  onWithdraw: () => void;
}

function AssignmentRow({ assignment, canAcknowledge, acknowledgement, onWithdraw }: AssignmentRowProps) {
  const statusColors: Record<string, string> = {
    assigned: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-gray-900">
          {assignment.assignee?.firstName} {assignment.assignee?.lastName}
        </p>
        <p className="text-xs text-gray-500">{assignment.assignee?.email}</p>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        <div>{assignment.module?.name}</div>
        {assignment.moduleVersion && (
          <VersionLabel version={assignment.moduleVersion.version} contentHash={assignment.moduleVersion.contentHash} />
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[assignment.status]}`}>
          {assignment.status.replace('_', ' ')}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {assignment.lastScoreOverall !== undefined ? `${assignment.lastScoreOverall}%` : '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : '-'}
      </td>
      {canAcknowledge && (
        <td className="px-4 py-3">
          {assignment.status === 'completed' ? (
            <AcknowledgeAssignment
              assignmentId={assignment.id}
              learnerUserId={assignment.assigneeUserId}
              existing={acknowledgement}
            />
          ) : (
            <span className="text-xs text-gray-400">After completion</span>
          )}
        </td>
      )}
      <td className="px-4 py-3">
        {/* A completed assignment is history; only an open one can be withdrawn. */}
        {assignment.status !== 'completed' && (
          <button type="button" onClick={onWithdraw} className="text-xs text-gray-400 hover:text-red-600" title="Withdraw">
            Withdraw
          </button>
        )}
      </td>
    </tr>
  );
}
